<template>
  <Accordion class="promo-accordion" :class="`promo-accordion--${variant}`">
    <AccordionItem
      v-for="(item, index) in accordionItems"
      :key="`item${index}`"
      :ref="item.ref"
      :id="`accordion-item-${item.ref}`"
      :index="index"
      :variant="variant"
      :isInitiallyOpen="item.isInitiallyOpen"
      :label="item.label"
      v-data-test-id="item.ref"
    >
      <template v-if="item.condition">
        <IconArrowDownBold v-if="!isBoxed" slot="icon" class="promo-accordion__icon" />
        <slot :name="item.ref" :events="{ updateAccordionHeight }"></slot>
      </template>
    </AccordionItem>

    <div v-if="hasPromotions && isBoxed" class="promo-accordion__total">
      {{ $t('total_left') }}
      <span class="promo-accordion__total-amount">
        {{ formatToCurrency(adyenTotalAmount / 100, locale, currency) }}
      </span>
    </div>
  </Accordion>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { isEmpty } from '@/helpers/object'
import { formatToCurrency } from '@/helpers/currency-formatter'
import Accordion from '@/components/_shared/Accordion.vue'
import AccordionItem, { VARIANTS } from '@/components/_shared/AccordionItem/AccordionItem.vue'
import IconArrowDownBold from '@/components/_icons/IconArrowDownBold.svg'

export default {
  name: 'PromoAccordion',
  components: {
    Accordion,
    AccordionItem,
    IconArrowDownBold,
  },

  props: {
    variant: {
      type: String,
      default: VARIANTS.small,
      validator(value) {
        return Object.values(VARIANTS).includes(value)
      },
    },
    giftCardsTotal: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      VARIANTS,
      formatToCurrency,
      isGiftCardEnabled: window.PARTIAL_PAYMENTS_ENABLED,
    }
  },

  computed: {
    ...mapState('store', ['currency']),
    ...mapState('page', ['locale']),
    ...mapState('order', ['adyenTotalAmount', 'promotionAdjustment']),

    ...mapGetters({
      total: 'order/total',
    }),

    hasPromotions() {
      return !isEmpty(this.promotionAdjustment) || this.giftCardsTotal !== 0
    },

    accordionItems() {
      const accordionItems = [
        {
          ref: 'gift',
          isInitiallyOpen: this.giftCardsTotal !== 0,
          label: this.$t('add_giftcard'),
          condition: !!this.$scopedSlots.gift && this.isGiftCardEnabled,
        },
        {
          ref: 'promo',
          isInitiallyOpen: !isEmpty(this.promotionAdjustment),
          label: this.$t('promo_code_add'),
          condition: !!this.$scopedSlots.promo,
        },
      ]

      return accordionItems.filter(item => item.condition)
    },

    isBoxed() {
      return this.variant === VARIANTS.boxed
    },
  },

  methods: {
    updateAccordionHeight(ref) {
      this.$refs[ref][0].updateHeight()
    },
  },
}
</script>

<style lang="scss" scoped>
.promo-accordion {
  &--boxed {
    margin-bottom: 24px;
  }

  &__icon {
    position: relative;
    width: 10px;
    height: 7px;
    margin-left: 10px;
    margin-top: 2px;

    /deep/ .accordion-item--open & {
      transform: rotate(180deg);
    }
  }

  &__total {
    display: flex;
    justify-content: space-between;
    padding: 24px 0;
    border-bottom: 1px solid $color-grey-light-5;
    font-size: $font-size-small;
  }

  &__total-amount {
    font-weight: bold;
  }
}
</style>

<i18n src="@/locales/promo_accordion.json"></i18n>
